import axios from 'axios';
import { Tree } from './Tree';
import { Rectangle } from './Rectangle';
import * as Constants from "../../constants"
import { DivIcon, LatLng, Map, Point } from 'leaflet';
import { useContext, useEffect, useState } from 'react';
import parameterContext from '../../context/parameterContext';


interface Report {
    id: string;
    lat: number;
    lng: number;
    temperature: number;
    windSpeed: number;
    windDirection: string;
    angle: number;
    width: number;

}

export const useTemperaturesWinds = () => {
    const [temperaturesWinds, setTemperaturesWinds] = useState<TemperaturesWinds | null>(null);
    const parameters = useContext(parameterContext);

    useEffect(() => {
        const moreData = parameters.more_temps_and_winds ?? false;
        const doNotSortTemps = parameters.doNotSortTemps ?? false;
        const instance = TemperaturesWinds.getInstance(moreData,doNotSortTemps);
        setTemperaturesWinds(instance);
    }, []);

    return temperaturesWinds;
};

export class TemperaturesWinds {
    private static instance: TemperaturesWinds;
    composAngleMap:any = {N: 0, E: 90, S: 180, W: 270}
    _tree:Tree | null = null;
    map:Map | null = null
    moreData: boolean = true;
    newFeed: boolean = false;
    doNotSortTemps: boolean = false;
    
    constructor(moreData: boolean, doNotSortTemps: boolean) {
        this.moreData = moreData;
        if (typeof newFeed !== 'undefined') {
            this.newFeed = newFeed;
        }
        this.doNotSortTemps = doNotSortTemps;
    }
    public static getInstance(moreData:boolean, doNotSortTemps: boolean): TemperaturesWinds {
        if (!TemperaturesWinds.instance) {
            TemperaturesWinds.instance = new TemperaturesWinds(moreData, doNotSortTemps);
        }
        return TemperaturesWinds.instance;
    }
    
     getTemperatures() {
        try {
            const reports= this.getReports();
            return this.getMarkersTemperatures(reports);
            
        } catch (error) {
            console.error(error);
            return [];

        }
        
    }

    getWinds() {
        try {
            const reports= this.getReports();
            return this.getMarkersWinds(reports);
            
        } catch (error) {
            console.error(error);
            return [];

        }
        
    }

    getMarkersWinds(reports: Report[]) {
        return reports.map((element) => {
            if(this.map && element.windSpeed!=undefined && element.angle!=undefined && !isNaN(element.angle)  && element.width!=undefined && element.windSpeed!=0 && element.windSpeed>1 && element.windSpeed<150) {
                const arrow = this.getArrow(element.lat, element.lng, element.width, element.angle);
                const icon = new DivIcon({
                    html: `<p class="winds-icon--number" >${element.windSpeed}</p>`,
                    className: 'winds-icon'
                });
                
                const point = this.map.latLngToLayerPoint(new LatLng(element.lat, element.lng));
                const angle = (element.angle != undefined) ? element.angle : 180;
                const startX = point.x+2;
                let startY = (angle>90 && angle<270) ? point.y-14 : point.y-5;
                startY = startY - 12;

                const positionElement = this.map.layerPointToLatLng(new Point(startX, startY));

                return {icon: icon, position:positionElement, arrow};
            }
            return null;
        });
    }
    
    getArrow(startLat: number, startLng: number, length: number, angle: number) {
        if(!this.map){
            return [];
        }
        const sides = this.getRectangleSides(angle, length);
        const point = this.map.latLngToLayerPoint(new LatLng(startLat, startLng));
        const endX = sides.sideHor+point.x;
        const endY = point.y-sides.sideVer;
        
        const pos = this.map.layerPointToLatLng([endX, endY]);
        
        const sideFirstPosition = this.getSidePosition(endX, endY, length*0.2, 90+angle+45);
        const sideSecondPosition = this.getSidePosition(endX, endY, length*0.2, 180+angle+45);
        
        return this.getLineObject(startLat, startLng, pos, sideFirstPosition, sideSecondPosition);
        
    }
    
    getLineObject(startLat: number, startLng: number, pos: LatLng, sideFirstPosition: LatLng, sideSecondPosition: LatLng){
        return  [
            [startLat, startLng],
            [pos.lat, pos.lng],
            [sideFirstPosition.lat, sideFirstPosition.lng],
            [pos.lat, pos.lng],
            [sideSecondPosition.lat, sideSecondPosition.lng]
        ];
    }
    
    getSidePosition (endX: number, endY: number, linesLength: number, angle: number) {
        if (!this.map) return new LatLng(0, 0);
        
        const sides = this.getRectangleSides(angle, linesLength);
        const side1X = endX+sides.sideHor;
        const side1Y = endY-sides.sideVer;
        return this.map.layerPointToLatLng([side1X, side1Y]);
    }

    getRectangleSides (angle: number, hypotenuse: number) {
        const radius = angle*(Math.PI/180);
        const sideHor = Math.sin(radius)*hypotenuse;
        const sideVer = Math.cos(radius)*hypotenuse;
        return {sideHor: sideHor, sideVer: sideVer};
    };
    
    getMarkersTemperatures(reports: Report[]) {
        return reports.map((element) => {
            if(!isNaN(element.temperature) && 
               element.temperature != 0 && 
               element.temperature < 150 && 
               element.temperature > -90) {
                
                const mainIcon = new DivIcon({
                    html: `<div style="color:${this.getColor(element.temperature).color}">${element.temperature}°</div>`,
                    className: 'temperature-icon'
                });
    
                const borderIcon = new DivIcon({
                    html: `<div style="color:${this.getColor(element.temperature).border}; -webkit-text-stroke:${this.getColor(element.temperature).border} 2px;">${element.temperature}°</div>`,
                    className: 'temperature-icon-border'
                });
    
                return {
                    temperature: element.temperature,
                    position: [element.lat, element.lng],
                    mainIcon,
                    borderIcon
                };
            }
        });
    }
    
    async init(map: Map) {
        await this.getRequest(map);
    }
    
    
    async getRequest(map: Map) {
        try {
            this.map = map;
            const response = await axios.get(`${ Constants.TEMPERATURES_AND_WINDS_API_URL ?? ''}?r=${Math.random()}${ this.newFeed ? '&newFeed=1' : ''}`);
            this.transformData(response.data);
        } catch (error) {
            console.error(error);
        }
    }
    
    transformData(data:string) {
        const items: any[] = [];
        const reports: any[] = [];
        const nonNationalItems: any[] = [];
        const lines: string[] = data.split( "<br/>" );
        const speedRation: number = 0.7;
        lines.forEach((line) => {
            if (!line) return;
            const params: string[] = line.split('#');
            if (params.length < 4) return;
            const windInfo: string[] = params[3].split("/");
            const angle :number = this.parseAngle(windInfo[0]);
            const width: number = speedRation * parseInt(windInfo[1]) + 10;
            const item :Report  = {
                id: params[0],
                lat: parseFloat(params[1]),
                lng: parseFloat(params[2]),
                temperature: parseInt(params[4]),
                windSpeed: parseInt(windInfo[1]),
                windDirection: windInfo[0],
                angle: angle,
                width: width
            };
            reports.push(item);
        });
        reports.sort((a,b) => (a.id[0] == "K") ? -1 : ((b.id[0] == "K") ? 1 : 0))
        let xMax = -Number.MAX_VALUE;
        let xMin = Number.MAX_VALUE;
        let yMax = -Number.MAX_VALUE;
        let yMin = Number.MAX_VALUE;
        reports.forEach((item) => {
            xMax = Math.max(xMax, item.lng);
            yMax = Math.max(yMax, item.lat);
            xMin = Math.min(xMin, item.lng);
            yMin = Math.min(yMin, item.lat);
        });
        const maxDim = Math.max(xMax - xMin, yMax - yMin);
        this._tree = new Tree(new Rectangle(xMin, yMin, maxDim, maxDim), 0);
        reports.forEach((item) => {
            if (this._tree) {
                this._tree.push( {x: item.lng, y: item.lat}, item)
            }
        });
    }

    getReports() {
        if(!this.map){
            return [];
        }
        const size = {width: this.map.getContainer().offsetWidth, height: this.map.getContainer().offsetHeight};

        const startPos = this.map.getBounds().getNorthWest()
        const endPos = this.map.getBounds().getSouthEast();

        const startLat = Math.min(startPos.lat, endPos.lat);
        const endLat = Math.max(startPos.lat, endPos.lat);
        let startLng = startPos.lng;
        let endLng = endPos.lng;
        startLng = this.correctLat(startLng);
        endLng = this.correctLat(endLng);
        
        let results: Report[] = [];
        const zoom = this.map.getZoom();
        const baseMultiplier = this.map.getContainer().offsetWidth < 768 ? 4.5 : 16;
        const tolerance = (endPos.lng - startPos.lng)/baseMultiplier;    
        

        let level = 0;
        if (this._tree) {
            level = Math.floor(Math.log(this._tree.getArea().width/tolerance)/Math.LN2);
        }
        
        if(this.moreData){
            level = level + 1;
        }
        let rect;
        if (this._tree) {
            if(startLng > endLng) {
                rect = new Rectangle(startLng, startLat, 180, endLat-startLat);
                this._tree.pull(rect, level, results);
                rect = new Rectangle( -180, startLat, endLng + 180, endLat-startLat);
                this._tree.pull(rect, level, results);
            }else{
                rect = new Rectangle(startLng, startLat, endLng-startLng, endLat-startLat);
                this._tree.pull(rect, level, results);
            }
        }

        const actualData: Report[] = [];
        const factor = Math.max(0.3, (20-zoom) * 0.4);
        const widthRatio = (15 * factor) / size.width;
        const heightRatio = (20 * factor) / size.height;
        const latDef = (endLat - startLat)*heightRatio;
        const lngDef = Math.abs(endLng - startLng)*widthRatio;
        
        if (!this.doNotSortTemps) {
            var resultsSorted: any[] = [];
            results.forEach((result) => {
                if(result.id[0] == "K"){
                    resultsSorted.push(result);
                }
            })
            
            results.forEach((result) => {
                if(resultsSorted.indexOf(result) == -1){
                    resultsSorted.push(result);
                }
            })
            results = resultsSorted;
        }
        
        results.forEach((result) => {
            if(this.checkPoint(result, actualData, latDef, lngDef)){
                actualData.push(result);
            }
        });
        
        return actualData;
    }

    // Using grid based approach to check the distance between points
    // by converting the lat/lng to pixel coordinates
    checkPoint(info: {lng: number, lat: number}, actualData: any[], latDef: number, lngDef: number) {
        const gridSize = this.map!.getContainer().offsetWidth < 768 ? 30 : 50;   
        const point = this.map!.latLngToContainerPoint([info.lat, info.lng]);
        
        for(let j = 0; j < actualData.length; j++) {
            const existingPoint = this.map!.latLngToContainerPoint([
                actualData[j].lat,
                actualData[j].lng
            ]);
            
            // Pixel-based distance checking
            const dx = point.x - existingPoint.x;
            const dy = point.y - existingPoint.y;
            const pixelDistance = Math.sqrt(dx * dx + dy * dy);
            
            if(pixelDistance < gridSize) {
                return false;
            }
        }
        return true;
    }
    
    correctLat(value: number) {
        let result;
        const ration = value > 0 ? -1 : 1;
        if(Math.abs(value/180)%2>=1)
            result = (180-Math.abs(value%180))*ration;
        else
            result = value%180;
        return result;
    }

    getColor = function(temperature: number) {
        var color =  "rgb( 255, 255, 255 )"
        let border = "rgb(84, 112, 185)";
        //Temperatures - (R, G, B)
        if( temperature  < -10 ){
            color = "rgb( 255, 255, 255 )";
            border = "rgb(84, 112, 185)";
        }
        if( temperature >= -10 && temperature < 0 ){
            color = "rgb( 255, 150, 255 )";
            border = "rgb(255, 255, 255)";
        }
        if( temperature >= 0 && temperature < 10 ){
            color = "rgb( 255, 0, 255 )";
            border = "rgb(255, 255, 255)";
        }
        if( temperature >= 10 && temperature < 20 ){
            color = "rgb( 175, 0, 255 )";
            border = "rgb(255, 255, 255)";
        }
        if( temperature >= 20 && temperature < 30 ){
            color = "rgb( 150, 100, 255 )";
            border = "rgb(255, 255, 255)";
        }
        if( temperature >= 30 && temperature < 40 ){
            color = "rgb( 0, 175, 255 )";
            border = "rgb(255, 255, 255)";
        }
        if( temperature >= 40 && temperature < 50 ){
            color = "rgb(123, 148, 148)";
            border = "rgb(255, 255, 255)";
        }
        if( temperature >= 50 && temperature < 60 ){
            color = "rgb( 0, 255, 0 )";
            border = "rgb(84, 112, 185)";
        }
        if( temperature >= 60 && temperature < 70 ){
            color = "rgb( 255, 214, 0 )";
            border = "rgb(84, 112, 185)";
        }
        if( temperature >= 70 && temperature < 80 ){
            color = "rgb( 255, 125, 0 )";
            border = "rgb(255, 255, 255)";
        }
        if( temperature >= 80 && temperature < 90 ){
            color = "rgb( 255, 0, 0 )";
            border = "rgb(255, 255, 255)";
        }
        if( temperature >= 90 && temperature < 100 ){
            color = "rgb( 175, 0, 0 )";
            border = "rgb(255, 255, 255)";
        }
        if( temperature >= 100 && temperature < 110 ){
            color = "rgb( 175, 0, 175 )";
            border = "rgb(255, 255, 255)";
        }
        if( temperature >= 110 ){
            color = "rgb( 225, 0, 225 )";   
            border = "rgb(255, 255, 255)";
        }

        /**
         -10 and colder        255, 255, 255
         0 to -10              255,150,255
         0 to 10               255, 0, 255
         10 to 20              175, 0, 255
         20 to 30              125, 0, 255
         30 to 40              0, 175, 255
         40 to 50              166, 219, 219
         50 to 60              0, 225, 0
         60 to 70              255, 255, 0
         70 to 80              255, 125, 0
         80 to 90              255, 0, 0
         90 to 100             175, 0, 0
         100 to 110            175, 0, 175
         110 and above         255, 0, 255
         **/
        return {color, border};
    };



    parseAngle(angleString: string) {
  
        let symbolE: string = angleString.charAt(angleString.length-1);
        let angle = this.composAngleMap[symbolE];
        let revers = false;
        for(let i= 1; i < angleString.length; i++) {
            symbolE = angleString.charAt(angleString.length-1-i);
            let symbolEAngle = this.composAngleMap[symbolE];
            if(symbolE == "N" && (revers || angle > 180)) {
                revers = true;
                symbolEAngle = 360;
            }
            angle -= (angle - symbolEAngle)/2;
        }
        return ( angle + 180 ) % 360;
    }
    
}
